/**
 * CR437: Flyer snippet hydration for deals.
 * 
 * Fetches from v_deal_flyer_snippets_v2 with deterministic row selection,
 * correct path normalization, and page image fallback.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { supabase } from "@/integrations/supabase/client";

export interface HydratedFlyerArtifact {
  snippet_url: string | null;
  thumbnail_url: string | null;
  page_url: string | null;
  page_number: number | null;
  source: "v_deal_flyer_snippets_v2";
  status: string | null;
  /** CR438: Raw storage paths for components that use useSignedStorageUrl */
  snippet_asset_path: string | null;
  page_image_path: string | null;
  /** CR447: Deterministic bucket-qualified storage paths */
  snippet_storage_path: string | null;
  page_storage_path: string | null;
  snippet_storage_path_norm: string | null;
  page_storage_path_norm: string | null;
}

/** CR437: Safe prefix strip — only removes leading flyer-snippets/ or leading slashes */
function stripBucketPrefix(path: string): string {
  if (!path) return path;
  let p = path.replace(/^\/+/, "");
  p = p.replace(/^flyer-snippets\//, "");
  return p;
}

/** CR436: Resolve page_image_path to correct bucket + object path */
function resolvePageBucket(raw: string): { bucket: string; objectPath: string } {
  if (raw.startsWith("flyer-poc-assets/")) {
    return { bucket: "flyer-poc-assets", objectPath: raw.replace(/^flyer-poc-assets\//, "") };
  }
  if (raw.startsWith("flyer-fetch-assets/")) {
    return { bucket: "flyer-fetch-assets", objectPath: raw.replace(/^flyer-fetch-assets\//, "") };
  }
  // rendered/... paths live in flyer-fetch-assets
  if (raw.startsWith("rendered/")) {
    return { bucket: "flyer-fetch-assets", objectPath: raw };
  }
  // Default to flyer-fetch-assets
  return { bucket: "flyer-fetch-assets", objectPath: raw };
}

export async function fetchFlyerSnippetForDeal(dealId: string): Promise<HydratedFlyerArtifact | null> {
  // CR437-A: Query the correct view
  // CR437-B: Deterministic row selection — order by created_at desc, limit 1
  const { data, error } = await supabase
    .from("v_deal_flyer_snippets_v2" as any)
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn("[CR437] fetchFlyerSnippetForDeal error:", error.message);
    return null;
  }
  if (!data) return null;

  // CR437-D: Prefer _norm columns as SSOT, fall back to base
  const snippetPath: string | null =
    (data as any).snippet_asset_path_norm ??
    (data as any).snippet_asset_path ??
    null;

  const pagePath: string | null =
    (data as any).page_image_path_norm ??
    (data as any).page_image_path ??
    null;

  const status: string | null =
    (data as any).flyer_asset_status_norm ??
    (data as any).flyer_asset_status ??
    null;

  // 1) Snippet URL (preferred) — CR437-C: safe stripBucketPrefix
  let snippetUrl: string | null = null;
  if (snippetPath) {
    const normalized = stripBucketPrefix(snippetPath);
    const { data: signed, error: signErr } = await supabase.storage
      .from("flyer-snippets")
      .createSignedUrl(normalized, 3600);

    if (!signErr && signed?.signedUrl) {
      snippetUrl = signed.signedUrl;
    } else {
      console.warn("[CR437] snippet sign failed:", dealId, signErr?.message);
    }
  }

  // 2) Page URL fallback (when snippet missing) — CR437-E
  let pageUrl: string | null = null;
  if (!snippetUrl && pagePath) {
    const { bucket, objectPath } = resolvePageBucket(pagePath);
    const { data: signed, error: signErr } = await supabase.storage
      .from(bucket)
      .createSignedUrl(objectPath, 3600);

    if (!signErr && signed?.signedUrl) {
      pageUrl = signed.signedUrl;
    } else {
      console.warn("[CR437] page sign failed:", dealId, bucket, objectPath, signErr?.message);
    }
  }

  console.log("[FlyerHydrate]", `dealId=${dealId}`, `status=${status}`, `snippet=${!!snippetUrl}`, `page=${!!pageUrl}`);

  return {
    snippet_url: snippetUrl,
    page_url: pageUrl,
    thumbnail_url: null,
    page_number: (data as any).page_number,
    source: "v_deal_flyer_snippets_v2",
    status,
    snippet_asset_path: snippetPath,
    page_image_path: pagePath,
    // CR447: Deterministic storage paths (bucket-qualified)
    snippet_storage_path: (data as any).snippet_storage_path ?? null,
    page_storage_path: (data as any).page_storage_path ?? null,
    snippet_storage_path_norm: (data as any).snippet_storage_path_norm ?? null,
    page_storage_path_norm: (data as any).page_storage_path_norm ?? null,
  };
}

// ================================================================
// CR447: BATCH HYDRATOR (no N+1)
// ================================================================

/**
 * CR447: Batch-hydrate flyer artifacts for an array of deals.
 * Single query to v_deal_flyer_snippets_v2, then attach to each deal.
 */
export async function hydrateFlyerArtifactsBatch<T extends Record<string, any>>(deals: T[]): Promise<T[]> {
  if (!deals.length) return deals;

  // Collect UUID deal IDs that don't already have flyer data
  const needsHydration: { index: number; dealId: string }[] = [];
  for (let i = 0; i < deals.length; i++) {
    const deal = deals[i];
    const fa = deal?.flyer_artifact ?? deal?.flyerArtifact ?? null;
    const hasData = fa?.snippet_url || fa?.snippetUrl || fa?.page_url || fa?.pageUrl || deal?.artifacts?.flyer_snippet?.url;
    if (hasData) continue;

    const dealId = deal?.id;
    const looksUuid = typeof dealId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dealId);
    if (!looksUuid) continue;

    needsHydration.push({ index: i, dealId });
  }

  if (!needsHydration.length) {
    console.log("[CR447][hydrateBatch] 0 deals need flyer hydration");
    return deals;
  }

  const dealIds = needsHydration.map(h => h.dealId);

  // Single batch query
  const { data, error } = await supabase
    .from("v_deal_flyer_snippets_v2" as any)
    .select("*")
    .in("deal_id", dealIds)
    .order("created_at", { ascending: false });

  if (error) {
    console.warn("[CR447][hydrateBatch] query error:", error.message);
    return deals;
  }

  // Group by deal_id, keep newest (first due to ORDER BY)
  const byDealId = new Map<string, any>();
  for (const row of (data || [])) {
    const did = (row as any).deal_id;
    if (!byDealId.has(did)) {
      byDealId.set(did, row);
    }
  }

  // Stats for debug logging
  const stats = { ready_snippet: 0, ready_page_only: 0, missing_all: 0, no_row: 0 };

  // Build result array
  const result = [...deals];
  for (const { index, dealId } of needsHydration) {
    const row = byDealId.get(dealId);
    if (!row) {
      stats.no_row++;
      continue;
    }

    const snippetPath: string | null = (row as any).snippet_asset_path_norm ?? (row as any).snippet_asset_path ?? null;
    const pagePath: string | null = (row as any).page_image_path_norm ?? (row as any).page_image_path ?? null;
    const status: string | null = (row as any).flyer_asset_status_norm ?? (row as any).flyer_asset_status ?? null;

    if (status === "ready_snippet") stats.ready_snippet++;
    else if (status === "ready_page_only") stats.ready_page_only++;
    else stats.missing_all++;

    const existingFa = result[index]?.flyer_artifact ?? result[index]?.flyerArtifact ?? {};

    result[index] = {
      ...result[index],
      flyer_artifact: {
        ...existingFa,
        snippet_url: null,
        page_url: null,
        page_number: (row as any).page_number,
        status,
        snippet_asset_path: snippetPath,
        snippet_asset_path_norm: snippetPath,
        page_image_path: pagePath,
        page_image_path_norm: pagePath,
        flyer_asset_status_norm: status,
        // CR447: Deterministic storage paths (bucket-qualified)
        snippet_storage_path: (row as any).snippet_storage_path ?? null,
        page_storage_path: (row as any).page_storage_path ?? null,
        snippet_storage_path_norm: (row as any).snippet_storage_path_norm ?? null,
        page_storage_path_norm: (row as any).page_storage_path_norm ?? null,
      },
    };
  }

  console.log(`[CR447][hydrateBatch] ${needsHydration.length} deals checked, rows found: ${byDealId.size}`, stats);
  return result;
}

/**
 * Hydrate a deal object with flyer snippet data if it's missing.
 * CR437-F: Attaches both snippet_url and page_url.
 * CR447: For batch operations, prefer hydrateFlyerArtifactsBatch instead.
 */
export async function hydrateDealFlyerIfMissing<T extends Record<string, any>>(deal: T): Promise<T> {
  const fa = deal?.flyer_artifact ?? deal?.flyerArtifact ?? null;
  const hasSnippet = fa?.snippet_url || fa?.snippetUrl;
  const hasPage = fa?.page_url || fa?.pageUrl;
  const hasArtifact = deal?.artifacts?.flyer_snippet?.url;

  if (hasSnippet || hasPage || hasArtifact) return deal; // Already has flyer data

  const dealId = deal?.id;
  // CR434: Skip hydration for non-UUID ids (sample deals, dedupe keys)
  const looksUuid = typeof dealId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dealId);
  if (!looksUuid) return deal;

  const snippet = await fetchFlyerSnippetForDeal(dealId);
  if (!snippet) return deal;

  return {
    ...deal,
      flyer_artifact: {
        ...(fa ?? {}),
        snippet_url: snippet.snippet_url,
        page_url: snippet.page_url,
        page_number: snippet.page_number,
        status: snippet.status,
        snippet_asset_path: snippet.snippet_asset_path,
        page_image_path: snippet.page_image_path,
        // CR447: Deterministic storage paths
        snippet_storage_path: snippet.snippet_storage_path,
        page_storage_path: snippet.page_storage_path,
        snippet_storage_path_norm: snippet.snippet_storage_path_norm,
        page_storage_path_norm: snippet.page_storage_path_norm,
      },
  };
}
